import React, { ReactElement, useMemo } from 'react';
import DropdownComponent from '../dropdown/dropdown.component';
import styles from './cart-quantity.dropdown.component.module.scss';

interface Props {
  readonly id: string;
  readonly disabled?: boolean;
  readonly onQuantityChange?: (newQuantity: number) => void;
  readonly quantity: number;
  readonly stockLevel: number;
  readonly minimumQuantity: number;
  readonly maximumQuantity: number;
  readonly quantityIncrements: number;
  readonly showOptionsOnTop?: boolean;
  readonly dataTrackId?: string;
  readonly dataTestId?: string;
  readonly showIcon?: boolean;
}

export default function CartQuantityDropdownComponent({
  id,
  disabled,
  onQuantityChange,
  quantity,
  stockLevel,
  minimumQuantity,
  maximumQuantity,
  quantityIncrements,
  showOptionsOnTop,
  dataTrackId,
  dataTestId,
  showIcon = true
}: Props): ReactElement {
  const quantityOptions: Array<number> = useMemo((): Array<number> => {
    const options: Array<number> = [];
    const max: number = Math.min(maximumQuantity, stockLevel);
    for (let option = minimumQuantity; option <= max; option += quantityIncrements) {
      options.push(option);
    }
    return options;
  }, [maximumQuantity, minimumQuantity, quantityIncrements, stockLevel]);

  const initialSelectedItem = quantityOptions.indexOf(quantity);

  return (
    <div
      className={styles['cart-quantity-dropdown']}
      data-track-id={dataTrackId}
      data-testid={dataTestId}
    >
      <DropdownComponent
        id={`${id}-dropdown`}
        disabled={disabled}
        onClick={async (clickedItemIdx: number): Promise<void> => {
          const newQuantity = quantityOptions[clickedItemIdx];
          if (onQuantityChange) {
            onQuantityChange(newQuantity);
          }
        }}
        placeholder={quantity.toString()}
        stretchToParent={true}
        initialSelectedItem={initialSelectedItem}
        showOnTop={showOptionsOnTop}
        showIcon={showIcon}
      >
        {quantityOptions.map((option: number) => (
          <span key={option}>{option}</span>
        ))}
      </DropdownComponent>
    </div>
  );
}
